import { doPost } from "../api/gateway/carexGateway";
import { decodeJWTToken } from "../lib/jwtLib";

export interface UserMetricEvent {
    idUser: number | null;
    idGroup: number | null;
    idPatient?: number | null;
    idFlow?: number | null;
    sessionId: string;
    eventCategory: string;
    eventType: string;
    eventName: string;
    eventData?: Record<string, any> | null;
    referenceId?: number | null;
    referenceType?: string | null;
    clientTimestamp: string;
}

/** Get decoded token data from localStorage (id, idGroup). Returns undefined if no token. */
export const getTokenData = (): { id: number; idGroup: number } | undefined => {
    const token = localStorage.getItem("Token");
    if (!token) return undefined;
    const decoded = decodeJWTToken(token) as { id?: number; idGroup?: number } | null;
    if (!decoded || decoded.id == null || decoded.idGroup == null) return undefined;
    return { id: decoded.id, idGroup: decoded.idGroup };
};

const toValidInteger = (v: unknown): number | null => {
    if (v == null) return null;
    if (typeof v === "number" && !isNaN(v)) return Math.floor(v);
    const n = Number(v);
    return !isNaN(n) ? Math.floor(n) : null;
};

/**
 * Track a user metric event (click, page view, etc.)
 * This function silently fails to avoid disrupting user experience.
 * idPatient and idFlow are normalized to integers; invalid values are omitted from the payload.
 */
export const trackUserMetricEvent = async (event: UserMetricEvent): Promise<void> => {
    try {
        const payload: UserMetricEvent = {
            idUser: event.idUser,
            idGroup: event.idGroup,
            sessionId: event.sessionId,
            eventCategory: event.eventCategory,
            eventType: event.eventType,
            eventName: event.eventName,
            clientTimestamp: event.clientTimestamp,
        };
        const idPatient = toValidInteger(event.idPatient);
        if (idPatient !== null) payload.idPatient = idPatient;
        const idFlow = toValidInteger(event.idFlow);
        if (idFlow !== null) payload.idFlow = idFlow;
        if (event.eventData != null) payload.eventData = event.eventData;
        if (event.referenceId != null) {
            const refId = toValidInteger(event.referenceId);
            if (refId !== null) payload.referenceId = refId;
        }
        if (event.referenceType != null) payload.referenceType = event.referenceType;

        await doPost("trackUserMetricEvent", {
            data: payload,
        });
    } catch (error) {
        // Silently fail - don't disrupt user experience with error logging
        // eslint-disable-next-line no-console
        console.debug("Failed to track user metric event:", error);
    }
};

/**
 * Track multiple user metric events in a batch
 * This function silently fails to avoid disrupting user experience
 */
export const trackUserMetricEventsBatch = async (events: UserMetricEvent[]): Promise<void> => {
    if (!events || events.length === 0) {
        return;
    }

    try {
        const normalized = events.map(event => {
            const p: Record<string, unknown> = {
                idUser: event.idUser,
                idGroup: event.idGroup,
                sessionId: event.sessionId,
                eventCategory: event.eventCategory,
                eventType: event.eventType,
                eventName: event.eventName,
                clientTimestamp: event.clientTimestamp,
            };
            const idPatient = toValidInteger(event.idPatient);
            if (idPatient !== null) p.idPatient = idPatient;
            const idFlow = toValidInteger(event.idFlow);
            if (idFlow !== null) p.idFlow = idFlow;
            if (event.eventData != null) p.eventData = event.eventData;
            if (event.referenceId != null) {
                const refId = toValidInteger(event.referenceId);
                if (refId !== null) p.referenceId = refId;
            }
            if (event.referenceType != null) p.referenceType = event.referenceType;
            return p;
        });
        await doPost("trackUserMetricEventsBatch", {
            data: { events: normalized },
        });
    } catch (error) {
        // Silently fail - don't disrupt user experience with error logging
        // eslint-disable-next-line no-console
        console.debug("Failed to track user metric events batch:", error);
    }
};

/**
 * Track a modal open or close event.
 * Convenience wrapper around `trackUserMetricEvent` with modal-specific defaults.
 *
 * @param modalName  - snake_case identifier for the modal (e.g. "search_patient")
 * @param action     - 'MODAL_OPEN' or 'MODAL_CLOSE'
 * @param extraData  - optional context such as patientId, encounterId, etc.
 */
export const trackModalEvent = (
    modalName: string,
    action: "MODAL_OPEN" | "MODAL_CLOSE",
    extraData?: Record<string, any>
): void => {
    const tokenData = getTokenData();
    const idUser = tokenData?.id ?? null;
    const idGroup = tokenData?.idGroup ?? null;

    if (!idUser || !idGroup) {
        return;
    }

    let sessionId = sessionStorage.getItem("userMetricsSessionId");
    if (!sessionId) {
        sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem("userMetricsSessionId", sessionId);
    }

    const rawIdPatient = extraData?.idPatient;
    const idPatient =
        rawIdPatient != null ? (typeof rawIdPatient === "number" ? rawIdPatient : Number(rawIdPatient)) : null;
    const validIdPatient = typeof idPatient === "number" && !isNaN(idPatient) ? idPatient : null;

    const event: UserMetricEvent = {
        idUser,
        idGroup,
        idFlow: extraData?.idFlow ?? null,
        sessionId,
        eventCategory: "navigation",
        eventType: action,
        eventName: `modal_${modalName}`,
        eventData: extraData ?? null,
        clientTimestamp: new Date().toISOString(),
    };
    if (validIdPatient !== null) {
        event.idPatient = validIdPatient;
    }

    trackUserMetricEvent(event);
};

/**
 * React hook-friendly helper that tracks modal open/close transitions.
 * Call inside a `useEffect` that watches the boolean open state.
 *
 * Usage:
 *   useEffect(() => {
 *       trackModalTransition(isOpen, prevOpenRef, 'search_patient');
 *   }, [isOpen]);
 */
export const trackModalTransition = (
    isOpen: boolean,
    prevRef: React.MutableRefObject<boolean>,
    modalName: string,
    extraData?: Record<string, any>
): void => {
    if (isOpen && !prevRef.current) {
        trackModalEvent(modalName, "MODAL_OPEN", extraData);
    } else if (!isOpen && prevRef.current) {
        trackModalEvent(modalName, "MODAL_CLOSE", extraData);
    }
    prevRef.current = isOpen;
};

/**
 * Track a search event (table filters, search inputs, etc.).
 * Uses eventCategory: 'interaction', eventType: 'SEARCH'.
 *
 * @param eventName - snake_case identifier (e.g. "patients_table_search")
 * @param eventData - context such as searchTerm, filters, table name
 */
export const trackSearchEvent = (eventName: string, eventData?: Record<string, any>): void => {
    const tokenData = getTokenData();
    const idUser = tokenData?.id ?? null;
    const idGroup = tokenData?.idGroup ?? null;

    if (!idUser || !idGroup) {
        return;
    }

    let sessionId = sessionStorage.getItem("userMetricsSessionId");
    if (!sessionId) {
        sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem("userMetricsSessionId", sessionId);
    }

    trackUserMetricEvent({
        idUser,
        idGroup,
        idPatient: eventData?.idPatient ?? null,
        idFlow: eventData?.idFlow ?? null,
        sessionId,
        eventCategory: "interaction",
        eventType: "SEARCH",
        eventName,
        eventData: eventData ?? null,
        clientTimestamp: new Date().toISOString(),
    });
};

/**
 * Track a form submission event.
 * Uses eventCategory: 'interaction', eventType: 'FORM_SUBMIT'.
 *
 * @param eventName - snake_case identifier (e.g. "protocol_form_submit")
 * @param eventData - context such as form type, ids, mode (create/edit)
 */
export const trackFormSubmitEvent = (eventName: string, eventData?: Record<string, any>): void => {
    const tokenData = getTokenData();
    const idUser = tokenData?.id ?? null;
    const idGroup = tokenData?.idGroup ?? null;

    if (!idUser || !idGroup) {
        return;
    }

    let sessionId = sessionStorage.getItem("userMetricsSessionId");
    if (!sessionId) {
        sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem("userMetricsSessionId", sessionId);
    }

    trackUserMetricEvent({
        idUser,
        idGroup,
        idPatient: eventData?.idPatient ?? null,
        idFlow: eventData?.idFlow ?? null,
        sessionId,
        eventCategory: "interaction",
        eventType: "FORM_SUBMIT",
        eventName,
        eventData: eventData ?? null,
        clientTimestamp: new Date().toISOString(),
    });
};

/**
 * Tracks LOGIN_FORM (form submit) and login_success_redirect (page view) events
 * after successful login. Resolves when both events are sent. Await before redirect.
 * Use when token is just received (decodedToken from jwt). Silently fails (resolves anyway).
 */
type DecodedToken = { id?: number; idGroup?: number };

export const trackLoginSuccessEvents = async (
    decodedToken: DecodedToken,
    targetPath: string,
    isDapperCare?: boolean
): Promise<void> => {
    const idUser = decodedToken?.id;
    const idGroup = decodedToken?.idGroup;

    if (idUser == null || idGroup == null || typeof idUser !== "number" || typeof idGroup !== "number") {
        return;
    }

    let sessionId = sessionStorage.getItem("userMetricsSessionId");
    if (!sessionId) {
        sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem("userMetricsSessionId", sessionId);
    }

    const clientTimestamp = new Date().toISOString();

    const formSubmitEvent: UserMetricEvent = {
        idUser,
        idGroup,
        sessionId,
        eventCategory: "interaction",
        eventType: "FORM_SUBMIT",
        eventName: "login_form_submit",
        eventData: { success: true },
        clientTimestamp,
    };

    const pageViewEvent: UserMetricEvent = {
        idUser,
        idGroup,
        sessionId,
        eventCategory: "navigation",
        eventType: "PAGE_VIEW",
        eventName: "login_success_redirect",
        eventData: { targetPath, isDapperCare: !!isDapperCare },
        clientTimestamp,
    };

    try {
        await Promise.all([trackUserMetricEvent(formSubmitEvent), trackUserMetricEvent(pageViewEvent)]);
    } catch {
        // Silent failure - do not disrupt login flow
    }
};
